import {
  getAuthSessionMarker,
  getAuthSessionTenantKey,
  getPageAuthSession,
  isStoredSessionCurrent,
} from './auth';
import { reportOperationalAlert } from './operationalAlerts';

export const GLOBAL_ERROR_TYPES = Object.freeze({
  react: 'frontend.react.render_failed',
  window: 'frontend.window.uncaught_error',
  promise: 'frontend.window.unhandled_rejection',
});

const REDACTED = '[redacted]';
const MAX_REASON_LENGTH = 500;

const stripUrlDetails = (value) => String(value || '').replace(
  /https?:\/\/[^\s<>"')\]]+/gi,
  (match) => {
    try {
      const parsed = new URL(match);
      return `${parsed.origin}${parsed.pathname}`;
    } catch {
      return match.split(/[?#]/, 1)[0];
    }
  }
);

export const sanitizeGlobalErrorText = (value, maxLength = MAX_REASON_LENGTH) => {
  if (typeof value !== 'string') return '';

  return stripUrlDetails(value)
    .replace(/\b(?:Bearer|Basic)\s+[A-Za-z0-9._~+/=-]+/gi, REDACTED)
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, REDACTED)
    .replace(/data:application\/pdf[^\s]*/gi, REDACTED)
    .replace(/%PDF-[\s\S]*/gi, REDACTED)
    .replace(
      /\b(access[_-]?token|api[_-]?key|auth(?:orization)?|credential|jwt|password|pin|secret|signature|pdfbase64)\s*[:=]\s*["']?[^\s,;"']+/gi,
      '$1=[redacted]'
    )
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, REDACTED)
    .replace(/\b\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}\b/g, REDACTED)
    .replace(
      /\b((?:pedido|order)(?:\s*(?:id|n[º°o]?))?\s*(?:[:=#-]\s*)?)[A-F0-9][A-Za-z0-9_-]{3,}/gi,
      '$1[redacted]'
    )
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi, REDACTED)
    .replace(/\b\d{6,}\b/g, REDACTED)
    .replace(/([?&](?:[^\s#=&]+)=)[^\s&#]*/g, '$1[redacted]')
    .replace(/#[^\s]*/g, '')
    .replace(/\b[A-Za-z]:\\Users\\[^\\\s]+/gi, 'C:\\Users\\[redacted]')
    .replace(/\/Users\/[^/\s]+/g, '/Users/[redacted]')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength);
};

export const isAbortError = (candidate) => {
  try {
    return candidate?.name === 'AbortError'
      || candidate?.reason?.name === 'AbortError';
  } catch {
    return false;
  }
};

const readErrorField = (candidate, field) => {
  try {
    return typeof candidate?.[field] === 'string' ? candidate[field] : '';
  } catch {
    return '';
  }
};

const getFirstComponentName = (componentStack) => {
  if (typeof componentStack !== 'string') return '';
  const match = componentStack.match(/(?:^|\n)\s*(?:at|in)\s+([A-Za-z][A-Za-z0-9_.-]{0,79})/);
  return match ? sanitizeGlobalErrorText(match[1], 80) : '';
};

const getSafeSourceFile = (value) => {
  if (typeof value !== 'string') return '';
  const withoutUrlDetails = stripUrlDetails(value).split(/[?#]/, 1)[0];
  const safe = sanitizeGlobalErrorText(withoutUrlDetails, 180);
  return safe.startsWith('/') || /^https?:\/\//i.test(safe) ? safe : '';
};

const buildSafeErrorDetails = (candidate, metadata = {}) => {
  const rawMessage = typeof candidate === 'string'
    ? candidate
    : readErrorField(candidate, 'message');
  const rawName = readErrorField(candidate, 'name');
  const component = getFirstComponentName(metadata.componentStack);
  const sourceFile = component ? '' : getSafeSourceFile(metadata.sourceFile);
  const reason = sanitizeGlobalErrorText(rawMessage || 'Erro não identificado no navegador.')
    || 'Erro não identificado no navegador.';

  return {
    ...(rawName ? { errorName: sanitizeGlobalErrorText(rawName, 80) } : null),
    reason,
    ...(component ? { component } : null),
    ...(sourceFile ? { sourceFile } : null),
  };
};

export const reportGlobalFrontendError = (
  type,
  candidate,
  metadata = {},
  dependencies = {}
) => {
  try {
    if (isAbortError(candidate)) return null;

    const session = (dependencies.getSession || getPageAuthSession)();
    const tenantKey = getAuthSessionTenantKey(session);
    const sessionKey = getAuthSessionMarker(session);
    if (!tenantKey || !sessionKey) return null;

    const sessionIsCurrent = dependencies.isSessionCurrent || isStoredSessionCurrent;
    if (!sessionIsCurrent(session)) return null;

    const reporter = dependencies.reporter || reportOperationalAlert;
    return reporter(tenantKey, {
      type,
      source: 'GlobalErrorTelemetry',
      severity: 'error',
      message: 'Falha não tratada na interface do navegador.',
      details: buildSafeErrorDetails(candidate, metadata),
    }, {
      sessionKey,
      isTenantCurrent: (currentTenantKey) => {
        const currentSession = (dependencies.getSession || getPageAuthSession)();
        return getAuthSessionTenantKey(currentSession) === currentTenantKey
          && getAuthSessionMarker(currentSession) === sessionKey
          && sessionIsCurrent(currentSession);
      },
    });
  } catch {
    return null;
  }
};

export const installGlobalErrorTelemetry = (
  windowRef = typeof window !== 'undefined' ? window : null,
  dependencies = {}
) => {
  if (!windowRef?.addEventListener) return () => {};

  const onError = (event) => {
    reportGlobalFrontendError(
      GLOBAL_ERROR_TYPES.window,
      event?.error || event?.message || '',
      { sourceFile: typeof event?.filename === 'string' ? event.filename : '' },
      dependencies
    );
  };
  const onUnhandledRejection = (event) => {
    reportGlobalFrontendError(
      GLOBAL_ERROR_TYPES.promise,
      event?.reason,
      {},
      dependencies
    );
  };

  try {
    windowRef.addEventListener('error', onError);
    windowRef.addEventListener('unhandledrejection', onUnhandledRejection);
  } catch {
    try {
      windowRef.removeEventListener?.('error', onError);
      windowRef.removeEventListener?.('unhandledrejection', onUnhandledRejection);
    } catch {
      // A inicializacao da aplicação não depende da telemetria.
    }
    return () => {};
  }

  return () => {
    try {
      windowRef.removeEventListener('error', onError);
      windowRef.removeEventListener('unhandledrejection', onUnhandledRejection);
    } catch {
      // A telemetria nunca pode interromper a interface.
    }
  };
};
